// Keep in sync with supabase/functions/_shared/email/catalog.ts.
// Coolify builds web/ alone, so this file must not import outside web/.

export const CLINIC_EMAIL_DOMAIN = 'clinio.app';

export const RESERVED_EMAIL_LOCAL_PARTS = [
  'admin',
  'support',
  'noreply',
  'postmaster',
  'abuse',
  'webmaster',
  'hostmaster',
  'security',
  'root',
  'mailer-daemon',
  'booking',
  'invoices',
  'invoice',
  'info',
  'hello',
  'contact', // English only. Danish "kontakt" is a normal clinic local-part.
  'help',
  'privacy',
  'legal',
  'clinio',
  'system',
  'mail',
  'email',
  'reply',
] as const;

export const EMAIL_LOCAL_PART_REGEX = /^[a-z0-9]([a-z0-9._-]{0,62}[a-z0-9])?$/;

export const SIGNATURE_TEXT_MAX = 2000;
export const SIGNATURE_HTML_MAX = 8000;
export const TEMPLATE_SUBJECT_MAX = 300;
export const TEMPLATE_BODY_MAX = 20000;

export const CPR_IN_TEXT_RE = /\b\d{6}-?\d{4}\b/;

export type EmailLocale = 'da' | 'en';

export const OVERRIDABLE_TEMPLATE_KEYS = [
  'appointment_confirmation',
  'appointment_rescheduled',
  'appointment_cancelled',
  'appointment_reminder',
  'booking_confirmation',
  'booking_notification',
  'manual_patient',
  'invoice',
  'invoice_reminder',
  'quote',
  'questionnaire_invitation',
  'questionnaire_reminder',
  'pre_visit_questionnaire',
  'after_visit_questionnaire',
  'portal_invitation',
  'portal_access_granted',
  'patient_message',
] as const;

export const CODE_ONLY_TEMPLATE_KEYS = [
  'staff_invitation',
  'password_reset',
  'password_changed',
] as const;

export type OverridableTemplateKey = (typeof OVERRIDABLE_TEMPLATE_KEYS)[number];
export type CodeOnlyTemplateKey = (typeof CODE_ONLY_TEMPLATE_KEYS)[number];
export type EmailTemplateKey = OverridableTemplateKey | CodeOnlyTemplateKey;

export const MERGE_FIELDS = [
  'patient_first_name',
  'patient_name',
  'clinic_name',
  'appointment_time',
  'clinician_name',
  'service_name',
  'confirmation_code',
  'invoice_number',
  'total',
  'due_date',
  'payment_link',
  'bank_details',
  'message',
  'questionnaire_name',
  'questionnaire_url',
  'expires_at',
  'expires_line',
  'portal_url',
  'accept_url',
  'reset_url',
  'inviter_name',
  'role_label',
  'patient_email',
  'patient_phone',
  'reminder_number',
  'quote_number',
  'video_meeting_line',
] as const;

export type MergeFieldName = (typeof MERGE_FIELDS)[number];
export type MergeFields = Partial<Record<MergeFieldName, string>>;

export type TemplateSource = {
  subject: string;
  body: string;
};

export type LocalPartIssue = 'empty' | 'invalid' | 'reserved' | 'too_long';

export function normalizeEmailLocalPart(raw: string): string {
  return raw.trim().toLowerCase();
}

export function localPartIssue(raw: string): LocalPartIssue | null {
  const norm = normalizeEmailLocalPart(raw);
  if (!norm) return 'empty';
  if (norm.length > 64) return 'too_long';
  if (norm.startsWith('reply+') || (RESERVED_EMAIL_LOCAL_PARTS as readonly string[]).includes(norm)) {
    return 'reserved';
  }
  if (!EMAIL_LOCAL_PART_REGEX.test(norm)) return 'invalid';
  return null;
}

export function clinicFromPreview(clinicName: string, localPart: string): string {
  const norm = normalizeEmailLocalPart(localPart);
  const addr = norm ? `${norm}@${CLINIC_EMAIL_DOMAIN}` : `…@${CLINIC_EMAIL_DOMAIN}`;
  const name = clinicName.trim() || 'Kliniknavn';
  return `${name} <${addr}>`;
}

export function containsCpr(value: string): boolean {
  return CPR_IN_TEXT_RE.test(value);
}

export function escapeHtml(s: string): string {
  return s
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

export function applyMergeFields(
  template: string,
  fields: MergeFields,
  mode: 'html' | 'text',
): string {
  return template.replace(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi, (_all, key: string) => {
    const val = fields[key as MergeFieldName] ?? '';
    return mode === 'html' ? escapeHtml(val) : val;
  });
}

const ALLOWED_TAGS = new Set([
  'p',
  'br',
  'strong',
  'em',
  'b',
  'i',
  'u',
  'a',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'span',
  'div',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
]);

function isSafeHref(href: string): boolean {
  const v = href.trim().toLowerCase();
  return v.startsWith('https://') || v.startsWith('http://') || v.startsWith('mailto:');
}

/** Allowlist sanitizer for clinic-authored HTML. Strips scripts, handlers, unsafe URLs. */
export function sanitizeClinicHtml(input: string): string {
  if (!input) return '';
  let html = input.replace(/<\/?(script|iframe|object|embed|form|link|meta|style)[^>]*>/gi, '');
  html = html.replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '');
  html = html.replace(/<\/?([a-z0-9]+)([^>]*)>/gi, (full, rawTag: string, rawAttrs: string) => {
    const tag = rawTag.toLowerCase();
    const closing = full.startsWith('</');
    if (!ALLOWED_TAGS.has(tag)) return '';
    if (closing) return `</${tag}>`;
    if (tag === 'br') return '<br>';
    if (tag === 'a') {
      const hrefMatch = rawAttrs.match(/\shref\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/i);
      const href = hrefMatch ? (hrefMatch[2] ?? hrefMatch[3] ?? hrefMatch[4] ?? '') : '';
      if (!href || !isSafeHref(href)) return '<a>';
      return `<a href="${escapeHtml(href)}">`;
    }
    return `<${tag}>`;
  });
  return html;
}

export function looksLikeHtml(value: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

export function bodyToHtml(body: string): string {
  const trimmed = body.trim();
  if (!trimmed) return '';
  if (looksLikeHtml(trimmed)) return sanitizeClinicHtml(trimmed);
  return trimmed
    .split(/\n{2,}/)
    .map((para) => `<p>${escapeHtml(para).replaceAll('\n', '<br>')}</p>`)
    .join('');
}

export function htmlToText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/(h[1-3]|li|tr)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function wrapEmailLayout(input: {
  locale: EmailLocale;
  clinicName: string;
  bodyHtml: string;
  signatureHtml?: string | null;
  signatureText?: string | null;
}): { html: string; text: string } {
  const lang = input.locale === 'en' ? 'en' : 'da';
  const name = escapeHtml(input.clinicName);
  const sigHtml = input.signatureHtml?.trim()
    ? `<div style="margin-top:24px;padding-top:16px;border-top:1px solid #E8E2D6;color:#5C564C;font-size:13px;line-height:1.5">${sanitizeClinicHtml(input.signatureHtml)}</div>`
    : input.signatureText?.trim()
      ? `<div style="margin-top:24px;padding-top:16px;border-top:1px solid #E8E2D6;color:#5C564C;font-size:13px;line-height:1.5;white-space:pre-wrap">${escapeHtml(input.signatureText)}</div>`
      : '';
  const html = `<!DOCTYPE html>
<html lang="${lang}">
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#F7F4EF;font-family:Inter,Helvetica,Arial,sans-serif;color:#1A1814">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#F7F4EF;padding:32px 0">
<tr><td align="center">
<table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:16px;overflow:hidden;border:1px solid #E8E2D6">
  <tr><td style="background:#0F6B63;padding:24px 32px">
    <span style="color:#ffffff;font-size:20px;font-weight:700">${name}</span>
  </td></tr>
  <tr><td style="padding:32px;font-size:15px;line-height:1.6">
    ${input.bodyHtml}
    ${sigHtml}
  </td></tr>
  <tr><td style="background:#F7F4EF;padding:16px 32px;font-size:11px;color:#8A8478;text-align:center">${name}</td></tr>
</table>
</td></tr>
</table>
</body>
</html>`;
  const sigText = input.signatureText?.trim()
    ? `\n\n--\n${input.signatureText.trim()}`
    : input.signatureHtml?.trim()
      ? `\n\n--\n${htmlToText(input.signatureHtml)}`
      : '';
  const text = `${htmlToText(input.bodyHtml)}${sigText}\n\n${input.clinicName}`;
  return { html, text };
}

const FIELDS_BY_TEMPLATE: Record<OverridableTemplateKey, MergeFieldName[]> = {
  appointment_confirmation: [
    'patient_first_name',
    'clinic_name',
    'appointment_time',
    'service_name',
    'clinician_name',
    'video_meeting_line',
  ],
  appointment_rescheduled: [
    'patient_first_name',
    'clinic_name',
    'appointment_time',
    'service_name',
    'clinician_name',
    'video_meeting_line',
  ],
  appointment_cancelled: [
    'patient_first_name',
    'clinic_name',
    'appointment_time',
    'service_name',
  ],
  appointment_reminder: [
    'patient_first_name',
    'clinic_name',
    'appointment_time',
    'service_name',
    'clinician_name',
    'video_meeting_line',
  ],
  booking_confirmation: [
    'patient_first_name',
    'clinic_name',
    'appointment_time',
    'service_name',
    'confirmation_code',
  ],
  booking_notification: [
    'clinic_name',
    'patient_name',
    'patient_email',
    'patient_phone',
    'appointment_time',
    'service_name',
    'confirmation_code',
  ],
  manual_patient: ['patient_first_name', 'clinic_name', 'message'],
  invoice: [
    'patient_first_name',
    'patient_name',
    'clinic_name',
    'invoice_number',
    'total',
    'due_date',
    'payment_link',
    'bank_details',
    'message',
  ],
  invoice_reminder: [
    'patient_first_name',
    'patient_name',
    'clinic_name',
    'invoice_number',
    'total',
    'due_date',
    'payment_link',
    'reminder_number',
    'message',
  ],
  quote: [
    'patient_first_name',
    'patient_name',
    'clinic_name',
    'quote_number',
    'total',
    'message',
    'accept_url',
  ],
  questionnaire_invitation: [
    'patient_first_name',
    'clinic_name',
    'questionnaire_name',
    'questionnaire_url',
    'expires_at',
    'expires_line',
  ],
  questionnaire_reminder: [
    'patient_first_name',
    'clinic_name',
    'questionnaire_name',
    'questionnaire_url',
    'expires_at',
    'expires_line',
  ],
  pre_visit_questionnaire: [
    'patient_first_name',
    'clinic_name',
    'questionnaire_name',
    'questionnaire_url',
    'appointment_time',
  ],
  after_visit_questionnaire: [
    'patient_first_name',
    'clinic_name',
    'questionnaire_name',
    'questionnaire_url',
  ],
  portal_invitation: [
    'patient_first_name',
    'clinic_name',
    'inviter_name',
    'accept_url',
    'expires_at',
  ],
  portal_access_granted: ['patient_first_name', 'clinic_name', 'portal_url'],
  // No message body. A clinic override cannot merge the portal message.
  patient_message: ['patient_first_name', 'clinic_name', 'portal_url'],
};

export function mergeFieldsForTemplate(key: OverridableTemplateKey): MergeFieldName[] {
  return FIELDS_BY_TEMPLATE[key];
}

const DEFAULTS: Record<OverridableTemplateKey, Record<EmailLocale, TemplateSource>> = {
  appointment_confirmation: {
    da: {
      subject: 'Aftale bekræftet — {{clinic_name}}',
      body: 'Hej {{patient_first_name}},\n\nDin aftale hos {{clinic_name}} er bekræftet.\n\nBehandling: {{service_name}}\nTidspunkt: {{appointment_time}}\nBehandler: {{clinician_name}}\n{{video_meeting_line}}',
    },
    en: {
      subject: 'Appointment confirmed — {{clinic_name}}',
      body: 'Hi {{patient_first_name}},\n\nYour appointment at {{clinic_name}} is confirmed.\n\nTreatment: {{service_name}}\nTime: {{appointment_time}}\nClinician: {{clinician_name}}\n{{video_meeting_line}}',
    },
  },
  appointment_rescheduled: {
    da: {
      subject: 'Aftale flyttet — {{clinic_name}}',
      body: 'Hej {{patient_first_name}},\n\nDin aftale hos {{clinic_name}} er flyttet.\n\nBehandling: {{service_name}}\nNyt tidspunkt: {{appointment_time}}\nBehandler: {{clinician_name}}\n{{video_meeting_line}}',
    },
    en: {
      subject: 'Appointment rescheduled — {{clinic_name}}',
      body: 'Hi {{patient_first_name}},\n\nYour appointment at {{clinic_name}} has been moved.\n\nTreatment: {{service_name}}\nNew time: {{appointment_time}}\nClinician: {{clinician_name}}\n{{video_meeting_line}}',
    },
  },
  appointment_cancelled: {
    da: {
      subject: 'Aftale aflyst — {{clinic_name}}',
      body: 'Hej {{patient_first_name}},\n\nDin aftale hos {{clinic_name}} er aflyst.\n\nBehandling: {{service_name}}\nOprindeligt tidspunkt: {{appointment_time}}',
    },
    en: {
      subject: 'Appointment cancelled — {{clinic_name}}',
      body: 'Hi {{patient_first_name}},\n\nYour appointment at {{clinic_name}} has been cancelled.\n\nTreatment: {{service_name}}\nOriginal time: {{appointment_time}}',
    },
  },
  appointment_reminder: {
    da: {
      subject: 'Påmindelse om aftale — {{clinic_name}}',
      body: 'Hej {{patient_first_name}},\n\nDette er en påmindelse om din aftale hos {{clinic_name}}.\n\nBehandling: {{service_name}}\nTidspunkt: {{appointment_time}}\nBehandler: {{clinician_name}}\n{{video_meeting_line}}',
    },
    en: {
      subject: 'Appointment reminder — {{clinic_name}}',
      body: 'Hi {{patient_first_name}},\n\nThis is a reminder of your appointment at {{clinic_name}}.\n\nTreatment: {{service_name}}\nTime: {{appointment_time}}\nClinician: {{clinician_name}}\n{{video_meeting_line}}',
    },
  },
  booking_confirmation: {
    da: {
      subject: 'Booking bekræftet — {{clinic_name}}',
      body: 'Hej {{patient_first_name}},\n\nDin online booking hos {{clinic_name}} er bekræftet.\n\nBehandling: {{service_name}}\nTidspunkt: {{appointment_time}}\nBekræftelseskode: {{confirmation_code}}',
    },
    en: {
      subject: 'Booking confirmed — {{clinic_name}}',
      body: 'Hi {{patient_first_name}},\n\nYour online booking at {{clinic_name}} is confirmed.\n\nTreatment: {{service_name}}\nTime: {{appointment_time}}\nConfirmation code: {{confirmation_code}}',
    },
  },
  booking_notification: {
    da: {
      subject: 'Ny online booking — {{patient_name}}',
      body: 'Der er oprettet en ny online booking hos {{clinic_name}}.\n\nPatient: {{patient_name}}\nE-mail: {{patient_email}}\nTelefon: {{patient_phone}}\nBehandling: {{service_name}}\nTidspunkt: {{appointment_time}}\nKode: {{confirmation_code}}',
    },
    en: {
      subject: 'New online booking — {{patient_name}}',
      body: 'A new online booking was created at {{clinic_name}}.\n\nPatient: {{patient_name}}\nEmail: {{patient_email}}\nPhone: {{patient_phone}}\nTreatment: {{service_name}}\nTime: {{appointment_time}}\nCode: {{confirmation_code}}',
    },
  },
  manual_patient: {
    da: {
      subject: 'Besked fra {{clinic_name}}',
      body: 'Hej {{patient_first_name}},\n\n{{message}}',
    },
    en: {
      subject: 'Message from {{clinic_name}}',
      body: 'Hi {{patient_first_name}},\n\n{{message}}',
    },
  },
  invoice: {
    da: {
      subject: 'Faktura {{invoice_number}} fra {{clinic_name}}',
      body: 'Hej {{patient_first_name}},\n\nDu har modtaget en faktura fra {{clinic_name}}.\n\n{{message}}\n\nFakturanummer: {{invoice_number}}\nBeløb: {{total}}\nForfaldsdato: {{due_date}}\n\n{{payment_link}}\n\n{{bank_details}}\n\nFakturaen er vedhæftet som PDF.',
    },
    en: {
      subject: 'Invoice {{invoice_number}} from {{clinic_name}}',
      body: 'Hi {{patient_first_name}},\n\nYou have received an invoice from {{clinic_name}}.\n\n{{message}}\n\nInvoice number: {{invoice_number}}\nAmount: {{total}}\nDue date: {{due_date}}\n\n{{payment_link}}\n\n{{bank_details}}\n\nThe invoice is attached as a PDF.',
    },
  },
  invoice_reminder: {
    da: {
      subject: 'Påmindelse {{reminder_number}}: Faktura {{invoice_number}} fra {{clinic_name}}',
      body: 'Hej {{patient_first_name}},\n\nDette er en påmindelse om faktura {{invoice_number}} fra {{clinic_name}}.\n\n{{message}}\n\nBeløb: {{total}}\nForfaldsdato: {{due_date}}\n\n{{payment_link}}',
    },
    en: {
      subject: 'Reminder {{reminder_number}}: Invoice {{invoice_number}} from {{clinic_name}}',
      body: 'Hi {{patient_first_name}},\n\nThis is a reminder about invoice {{invoice_number}} from {{clinic_name}}.\n\n{{message}}\n\nAmount: {{total}}\nDue date: {{due_date}}\n\n{{payment_link}}',
    },
  },
  quote: {
    da: {
      subject: 'Tilbud {{quote_number}} fra {{clinic_name}}',
      body: 'Hej {{patient_first_name}},\n\nDu har modtaget et tilbud fra {{clinic_name}}.\n\n{{message}}\n\nTilbudsnummer: {{quote_number}}\nBeløb: {{total}}\n\nSe tilbuddet: {{accept_url}}',
    },
    en: {
      subject: 'Quote {{quote_number}} from {{clinic_name}}',
      body: 'Hi {{patient_first_name}},\n\nYou have received a quote from {{clinic_name}}.\n\n{{message}}\n\nQuote number: {{quote_number}}\nAmount: {{total}}\n\nView the quote: {{accept_url}}',
    },
  },
  questionnaire_invitation: {
    da: {
      subject: 'Spørgeskema fra {{clinic_name}}',
      body: 'Hej {{patient_first_name}},\n\n{{clinic_name}} har sendt dig spørgeskemaet "{{questionnaire_name}}".\n\nUdfyld det her: {{questionnaire_url}}\n\n{{expires_line}}',
    },
    en: {
      subject: 'Questionnaire from {{clinic_name}}',
      body: 'Hi {{patient_first_name}},\n\n{{clinic_name}} has sent you the questionnaire "{{questionnaire_name}}".\n\nFill it out here: {{questionnaire_url}}\n\n{{expires_line}}',
    },
  },
  questionnaire_reminder: {
    da: {
      subject: 'Påmindelse: spørgeskema fra {{clinic_name}}',
      body: 'Hej {{patient_first_name}},\n\nDette er en påmindelse om spørgeskemaet "{{questionnaire_name}}" fra {{clinic_name}}.\n\nUdfyld det her: {{questionnaire_url}}\n\n{{expires_line}}',
    },
    en: {
      subject: 'Reminder: questionnaire from {{clinic_name}}',
      body: 'Hi {{patient_first_name}},\n\nThis is a reminder about the questionnaire "{{questionnaire_name}}" from {{clinic_name}}.\n\nFill it out here: {{questionnaire_url}}\n\n{{expires_line}}',
    },
  },
  pre_visit_questionnaire: {
    da: {
      subject: 'Spørgeskema før din aftale hos {{clinic_name}}',
      body: 'Hej {{patient_first_name}},\n\nUdfyld venligst "{{questionnaire_name}}" inden din aftale hos {{clinic_name}} ({{appointment_time}}).\n\n{{questionnaire_url}}',
    },
    en: {
      subject: 'Questionnaire before your appointment at {{clinic_name}}',
      body: 'Hi {{patient_first_name}},\n\nPlease complete "{{questionnaire_name}}" before your appointment at {{clinic_name}} ({{appointment_time}}).\n\n{{questionnaire_url}}',
    },
  },
  after_visit_questionnaire: {
    da: {
      subject: 'Spørgeskema efter dit besøg hos {{clinic_name}}',
      body: 'Hej {{patient_first_name}},\n\n{{clinic_name}} har sendt dig spørgeskemaet "{{questionnaire_name}}" efter dit besøg.\n\n{{questionnaire_url}}',
    },
    en: {
      subject: 'Questionnaire after your visit at {{clinic_name}}',
      body: 'Hi {{patient_first_name}},\n\n{{clinic_name}} has sent you the questionnaire "{{questionnaire_name}}" after your visit.\n\n{{questionnaire_url}}',
    },
  },
  portal_invitation: {
    da: {
      subject: 'Adgang til patientportalen hos {{clinic_name}}',
      body: 'Hej {{patient_first_name}},\n\n{{inviter_name}} har givet dig adgang til patientportalen hos {{clinic_name}}.\n\nOpret adgangskode: {{accept_url}}\n\nLinket udløber {{expires_at}}.',
    },
    en: {
      subject: 'Access to the patient portal at {{clinic_name}}',
      body: 'Hi {{patient_first_name}},\n\n{{inviter_name}} has given you access to the patient portal at {{clinic_name}}.\n\nCreate a password: {{accept_url}}\n\nThe link expires {{expires_at}}.',
    },
  },
  portal_access_granted: {
    da: {
      subject: 'Du har nu adgang til {{clinic_name}} i patientportalen',
      body: 'Hej {{patient_first_name}},\n\nDu har nu adgang til {{clinic_name}} i patientportalen.\n\nLog ind her: {{portal_url}}',
    },
    en: {
      subject: 'You now have access to {{clinic_name}} in the patient portal',
      body: 'Hi {{patient_first_name}},\n\nYou now have access to {{clinic_name}} in the patient portal.\n\nSign in here: {{portal_url}}',
    },
  },
  patient_message: {
    da: {
      subject: 'Du har en ny besked fra {{clinic_name}}',
      body: 'Hej {{patient_first_name}},\n\nDu har en ny besked fra {{clinic_name}}.\n\nLæs den i patientportalen: {{portal_url}}',
    },
    en: {
      subject: 'You have a new message from {{clinic_name}}',
      body: 'Hi {{patient_first_name}},\n\nYou have a new message from {{clinic_name}}.\n\nRead it in the patient portal: {{portal_url}}',
    },
  },
};

export function defaultTemplateSource(
  key: OverridableTemplateKey,
  locale: EmailLocale,
): TemplateSource {
  return DEFAULTS[key][locale];
}

/** Saved Indstillinger → Skabeloner row, or the Clinio default for that language. */
export function clinicTemplateSource(
  key: OverridableTemplateKey,
  locale: EmailLocale,
  stored: { subject: string; body_html: string } | null | undefined,
): TemplateSource {
  const subject = stored?.subject?.trim() ?? '';
  const body = stored?.body_html?.trim() ?? '';
  if (subject && body) return { subject, body };
  return defaultTemplateSource(key, locale);
}

export function sampleMergeFields(
  key: OverridableTemplateKey,
  locale: EmailLocale,
): MergeFields {
  const da = locale === 'da';
  const all: MergeFields = {
    patient_first_name: da ? 'Anna' : 'Anna',
    patient_name: da ? 'Anna Jensen' : 'Anna Jensen',
    clinic_name: da ? 'Eksempel Klinik' : 'Example Clinic',
    appointment_time: da ? '22. oktober 2026 kl. 10:00' : '22 October 2026 at 10:00',
    clinician_name: da ? 'Lars Hansen' : 'Lars Hansen',
    service_name: da ? 'Konsultation' : 'Consultation',
    confirmation_code: 'AB12CD',
    invoice_number: 'F-1001',
    total: da ? '1.150 kr.' : 'DKK 1,150',
    due_date: da ? '5. november 2026' : '5 November 2026',
    payment_link: 'https://pay.clinio.app/example',
    bank_details: da ? 'Reg. 1234 Konto 1234567890' : 'Reg. 1234 Account 1234567890',
    message: da ? 'Valgfri besked fra klinikken.' : 'Optional message from the clinic.',
    questionnaire_name: da ? 'Helbredsskema' : 'Health questionnaire',
    questionnaire_url: 'https://forms.clinio.app/example',
    expires_at: da ? '1. november 2026' : '1 November 2026',
    expires_line: da ? 'Frist: 1. november 2026' : 'Deadline: 1 November 2026',
    portal_url: 'https://v2.clinio.app/portal',
    accept_url: 'https://v2.clinio.app/accept-invitation',
    reset_url: 'https://v2.clinio.app/reset-password',
    inviter_name: da ? 'Klinikken' : 'The clinic',
    role_label: da ? 'Medarbejder' : 'Staff',
    patient_email: 'anna@example.com',
    patient_phone: '20 12 34 56',
    reminder_number: '1',
    quote_number: 'T-1001',
    video_meeting_line: da
      ? 'Videomøde: https://clinio.whereby.com/example'
      : 'Video meeting: https://clinio.whereby.com/example',
  };
  const allowed = new Set(mergeFieldsForTemplate(key));
  const next: MergeFields = {};
  for (const field of MERGE_FIELDS) {
    if (allowed.has(field)) next[field] = all[field];
  }
  return next;
}

/**
 * A deadline paragraph that is only "Frist:" or "Deadline:" after an empty
 * {{expires_at}}. The date itself stays. Other labels, such as "Linket udløber",
 * are left as written.
 */
export function omitEmptyDeadlineLabel(body: string): string {
  const withoutParagraphs = body.replace(
    /<p\b[^>]*>\s*(?:frist|deadline)\s*:\s*<\/p>/gi,
    '',
  );
  const lines = withoutParagraphs
    .split('\n')
    .filter((line) => !/^\s*(?:frist|deadline)\s*:\s*$/i.test(line));
  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

export function renderTemplateSource(
  source: TemplateSource,
  locale: EmailLocale,
  fields: MergeFields,
  clinicName: string,
  signature?: { html?: string | null; text?: string | null },
): { subject: string; html: string; text: string } {
  const subject = applyMergeFields(source.subject, fields, 'text').trim();
  const bodyMerged = omitEmptyDeadlineLabel(applyMergeFields(source.body, fields, 'text'));
  const bodyHtml = bodyToHtml(bodyMerged);
  const wrapped = wrapEmailLayout({
    locale,
    clinicName,
    bodyHtml,
    signatureHtml: signature?.html,
    signatureText: signature?.text,
  });
  return { subject, html: wrapped.html, text: wrapped.text };
}

export function isOverridableTemplateKey(value: string): value is OverridableTemplateKey {
  return (OVERRIDABLE_TEMPLATE_KEYS as readonly string[]).includes(value);
}

export function localeFromClinic(value: string | null | undefined): EmailLocale {
  const v = (value ?? "").toLowerCase();
  return v.startsWith("en") ? "en" : "da";
}
